import PageHero from "@/components/PageHero";
import CTA from "@/components/CTA";
import WorkflowTimeline from "@/components/WorkflowTimeline";
import { motion } from "framer-motion";
import { FileText, Upload, ClipboardList, Workflow, ShieldCheck, Truck, Receipt, CheckCircle2 } from "lucide-react";

const STEPS = [
  { icon: FileText, title: "Create Rx", desc: "Doctors create a structured, digital Rx in under a minute." },
  { icon: Upload, title: "Upload Files", desc: "STLs, photos, and notes, attached, versioned, and easy to preview." },
  { icon: ClipboardList, title: "Assign Products", desc: "Materials, shade, quantities, and priority, all captured up front." },
  { icon: Workflow, title: "Production", desc: "Assigned across technicians and stations with a live timeline." },
  { icon: ShieldCheck, title: "Quality", desc: "Structured QA steps with photos, sign-offs, and audit trail." },
  { icon: Truck, title: "Delivery", desc: "Optimized driver routes and digital proof of delivery." },
  { icon: Receipt, title: "Billing", desc: "Auto-invoicing from the Rx and delivery, reconciled and exportable." },
  { icon: CheckCircle2, title: "Complete", desc: "The case is closed, filed, and searchable, with a full audit trail." },
];

export default function HowItWorks() {
  return (
    <>
      <PageHero
        chapter="§ 05"
        eyebrow="How it works"
        title={"One connected system for every stage of your case"}
        description="No separate tools, no gaps between steps. Just one system, carrying your case from the first Rx to the last delivery."
      />

      <section data-testid="how-timeline" className="pb-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-brand-cyan via-brand-plum to-brand-rose opacity-40" />
            <div className="space-y-10">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={s.title}
                    data-testid={`how-step-${i}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: i * 0.06 }}
                    className="pl-24 relative"
                  >
                    <div className="absolute left-0 top-0 h-16 w-16 rounded-2xl glass hairline glow-brand inline-flex items-center justify-center">
                      <Icon className="h-5 w-5 text-brand-plum" />
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                      Step {i + 1}
                    </div>
                    <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold tracking-[-0.02em]">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-xl leading-relaxed">
                      {s.desc}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <WorkflowTimeline />
      <CTA />
    </>
  );
}
